<template>
  <template v-if="showNavigation">
    <Transition name="plain-navigation-backdrop">
      <div v-if="mobileOpen || desktopExpanded || hoverExpanded" class="plain-admin-overlay" aria-hidden="true" @click="closeNavigation"></div>
    </Transition>

    <aside class="plain-admin-sidebar" :class="{ 'is-mobile-open': mobileOpen, 'is-desktop-expanded': desktopExpanded || hoverExpanded }" :aria-label="`${brand.productName} navigation`" @mouseenter="openHoverNavigation" @mouseleave="closeHoverNavigation">
      <router-link to="/" class="plain-admin-brand" :title="brand.productName" @click="closeNavigation">
        <img class="plain-admin-brand-icon" :src="brand.assets.logoMark" :alt="brand.productName" />
        <img class="plain-admin-brand-full" :src="brand.assets.logo" :alt="brand.productName" />
      </router-link>

      <nav class="plain-admin-navigation" aria-label="Main navigation">
        <router-link v-for="item in navigationItems" :key="item.to" :to="{ name: item.to }"
          class="plain-admin-nav-link" :class="{ 'is-active': isActiveModule(item.to) }" :aria-label="item.title"
          @click="closeNavigation">
          <component :is="moduleIcon(item.to)" :size="19" />
          <span>{{ item.title }}</span>
        </router-link>
      </nav>

      <div class="plain-admin-sidebar-footer">
        <button type="button" class="plain-admin-profile-trigger" :title="user?.name" @click="userMenuOpen = !userMenuOpen">
          <img v-if="user?.profile_img" :src="user.profile_img" alt="" />
          <span v-else>{{ userInitials }}</span>
        </button>
      </div>
    </aside>

    <header class="plain-admin-header">
      <button type="button" class="plain-admin-icon-button plain-admin-navigation-trigger"
        :aria-label="desktopExpanded || mobileOpen ? 'Close navigation' : 'Open navigation'"
        :aria-expanded="desktopExpanded || mobileOpen" @click="toggleNavigation">
        <PanelLeft :size="18" />
      </button>

      <span class="plain-admin-header-divider" aria-hidden="true"></span>

      <nav class="plain-admin-header-navigation" aria-label="Workspace shortcuts">
        <router-link v-for="item in headerNavigationItems" :key="item.to" :to="{ name: item.to }"
          :class="{ 'is-active': isActiveModule(item.to) }">
          {{ item.to === 'home' ? 'Overview' : item.title }}
        </router-link>
      </nav>

      <div class="plain-admin-search">
        <Search :size="18" aria-hidden="true" />
        <input ref="searchInput" v-model.trim="navSearch" type="search" placeholder="Search modules..." aria-label="Search modules" @keydown.enter.prevent="openFirstSearchResult" />
        <kbd>Ctrl K</kbd>
        <div v-if="navSearch && filteredNavigationItems.length" class="plain-admin-search-results">
          <button v-for="item in filteredNavigationItems" :key="item.to" type="button" @click="openSearchResult(item.to)">
            <component :is="moduleIcon(item.to)" :size="16" />
            <span>{{ item.title }}</span>
          </button>
        </div>
      </div>

      <div class="plain-admin-header-actions">
        <button type="button" class="plain-admin-icon-button" :aria-label="isDark ? 'Use light theme' : 'Use dark theme'" @click="toggleTheme">
          <Sun v-if="isDark" :size="18" />
          <Moon v-else :size="18" />
        </button>
        <router-link v-if="hasSettings" to="/settings" class="plain-admin-icon-button" title="Settings" aria-label="Settings">
          <Settings :size="18" />
        </router-link>
        <button type="button" class="plain-admin-avatar-button" aria-label="Open user menu" @click="userMenuOpen = !userMenuOpen">{{ userInitials }}</button>
      </div>
    </header>

    <div v-if="userMenuOpen" class="plain-admin-user-menu">
      <div class="plain-admin-user-summary">
        <strong>{{ user?.name }}</strong>
        <small>{{ user?.designation || user?.account?.name || brand.workspaceLabel }}</small>
      </div>
      <router-link to="/user/profile" @click="userMenuOpen = false"><UserRound :size="16" /> Profile</router-link>
      <router-link v-if="hasSettings" to="/settings" @click="userMenuOpen = false"><Settings :size="16" /> Settings</router-link>
      <button type="button" @click="handleSignOut"><LogOut :size="16" /> Sign out</button>
    </div>
  </template>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { LogOut, Moon, PanelLeft, Search, Settings, Sun, UserRound } from "@lucide/vue";
import { addModules } from "@/composables/cache/modules";
import useAuthorizationRealtime from "@/composables/useAuthorizationRealtime";
import brand from "@/whitelabel/brand.config";
import { moduleIcon } from "@/ui/templates/plain-admin/module-icons";

export default {
  name: "PlainAdminNavigation",
  components: { LogOut, Moon, PanelLeft, Search, Settings, Sun, UserRound },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    useAuthorizationRealtime();
    const mobileOpen = ref(false);
    const desktopExpanded = ref(false);
    const hoverExpanded = ref(false);
    let hoverOpenTimer = null;
    const userMenuOpen = ref(false);
    const navSearch = ref("");
    const searchInput = ref(null);
    const isDark = ref(false);
    const showNavigation = computed(() => !["setup", "login", "utility", "loading", "logout"].some((part) => route.path.includes(part)));
    const user = computed(() => store.state.user || null);
    const tabs = computed(() => {
      if (!user.value?.modules) return [];
      return addModules().filter((item) => user.value.modules.includes(item.to) && item.type === user.value.type);
    });
    const navigationItems = computed(() => [{ to: "home", title: "Dashboard" }, ...tabs.value]);
    const headerNavigationItems = computed(() => [
      { to: "home", title: "Dashboard" },
      ...(store.state.headerModuleShortcuts || []).map(name => tabs.value.find(item => item.to === name)).filter(Boolean),
    ]);
    const filteredNavigationItems = computed(() => {
      const term = navSearch.value.toLowerCase();
      return term ? navigationItems.value.filter((item) => `${item.title} ${item.to}`.toLowerCase().includes(term)).slice(0, 7) : [];
    });
    const routeModuleName = computed(() => {
      const firstPath = route.path.split("/").filter(Boolean)[0] || "home";
      return { "tariff-mini": "tariffMini", "tariff-plus": "tariffPlus", care: route.name }[firstPath] || firstPath;
    });
    watch([routeModuleName, tabs], ([moduleName, availableTabs]) => {
      const item = availableTabs.find(item => item.to === moduleName || item.to === route.name);
      if (item && showNavigation.value) store.commit("visitHeaderModule", item.to);
    }, { immediate: true });
    const userInitials = computed(() => String(user.value?.name || brand.shortName).split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase());
    const hasSettings = computed(() => tabs.value.some((item) => item.to === "settings"));
    const isActiveModule = (name) => name === "home" ? route.path === "/" : route.name === name || routeModuleName.value === name;
    const openSearchResult = (name) => { navSearch.value = ""; router.push({ name }); };
    const openFirstSearchResult = () => { if (filteredNavigationItems.value[0]) openSearchResult(filteredNavigationItems.value[0].to); };
    const closeHoverNavigation = () => {
      clearTimeout(hoverOpenTimer);
      hoverOpenTimer = null;
      hoverExpanded.value = false;
    };
    const closeNavigation = () => { mobileOpen.value = false; desktopExpanded.value = false; closeHoverNavigation(); };
    const openHoverNavigation = () => {
      clearTimeout(hoverOpenTimer);
      if (window.matchMedia("(min-width: 768px) and (hover: hover) and (pointer: fine)").matches) {
        hoverOpenTimer = setTimeout(() => { hoverExpanded.value = true; hoverOpenTimer = null; }, 220);
      }
    };
    const toggleNavigation = () => {
      if (window.matchMedia("(max-width: 767px)").matches) mobileOpen.value = !mobileOpen.value;
      else desktopExpanded.value = !desktopExpanded.value;
    };
    const toggleTheme = () => {
      isDark.value = !isDark.value;
      document.documentElement.classList.toggle("dark", isDark.value);
      window.localStorage.setItem("plain-admin-theme", isDark.value ? "dark" : "light");
    };
    const handleSignOut = () => { userMenuOpen.value = false; router.push({ name: "logout" }); };
    const handleSearchShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchInput.value?.focus();
      }
    };

    onMounted(() => {
      isDark.value = window.localStorage.getItem("plain-admin-theme") === "dark";
      document.documentElement.classList.toggle("dark", isDark.value);
      window.addEventListener("keydown", handleSearchShortcut);
    });
    onUnmounted(() => { window.removeEventListener("keydown", handleSearchShortcut); clearTimeout(hoverOpenTimer); });
    watch(() => route.fullPath, () => { closeNavigation(); userMenuOpen.value = false; navSearch.value = ""; });
    watchEffect(() => {
      if (!store.state.user && route.name !== "login" && route.name !== "logout") router.replace({ name: "login" });
    });

    return { brand, closeNavigation, desktopExpanded, hoverExpanded, openHoverNavigation, closeHoverNavigation, filteredNavigationItems, handleSignOut, hasSettings,
      headerNavigationItems, isActiveModule, isDark, mobileOpen, moduleIcon, navigationItems, navSearch,
      openFirstSearchResult, openSearchResult, showNavigation, toggleNavigation, toggleTheme, user,
      userInitials, userMenuOpen, searchInput };
  },
};
</script>

<style scoped>
.plain-admin-overlay { background: rgb(15 23 42 / .18); inset: 0; position: fixed; z-index: 80; }
.plain-navigation-backdrop-enter-active, .plain-navigation-backdrop-leave-active { transition: opacity 240ms ease; }
.plain-navigation-backdrop-enter-from, .plain-navigation-backdrop-leave-to { opacity: 0; }
.plain-admin-sidebar { align-items: center; background: var(--ui-background); border-right: 1px solid var(--ui-sidebar-border); bottom: 0; display: flex; flex-direction: column; left: 0; padding: 1.25rem .875rem; position: fixed; top: 0; transition: width 420ms cubic-bezier(.4, 0, .2, 1), box-shadow 420ms ease; width: var(--ui-sidebar-collapsed-width); z-index: 90; }
.plain-admin-brand { align-items: center; background: var(--ui-primary); border-radius: var(--ui-radius); display: flex; height: 2.5rem; justify-content: center; width: 2.5rem; }
.plain-admin-brand img { height: 1.75rem; object-fit: contain; width: 1.75rem; }
.plain-admin-brand .plain-admin-brand-full { display: none; }
.plain-admin-navigation { align-items: center; display: flex; flex: 1; flex-direction: column; gap: .5rem; margin-top: 1.25rem; min-height: 0; overflow-y: auto; scrollbar-width: none; width: 100%; }
.plain-admin-navigation::-webkit-scrollbar { display: none; }
.plain-admin-nav-link { align-items: center; border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-muted-foreground); display: flex; flex: 0 0 auto; height: 2.5rem; justify-content: center; position: relative; text-decoration: none; width: 2.5rem; }
.plain-admin-nav-link span { background: var(--ui-popover); border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 2px); box-shadow: var(--ui-shadow-sm); color: var(--ui-popover-foreground); font-size: .75rem; font-weight: 600; left: 3rem; opacity: 0; padding: .4rem .55rem; pointer-events: none; position: absolute; transform: translateX(-.25rem); transition: 120ms ease; white-space: nowrap; z-index: 120; }
.plain-admin-nav-link:hover, .plain-admin-nav-link.is-active { background: var(--ui-sidebar-accent); color: var(--ui-sidebar-accent-foreground); }
.plain-admin-nav-link:hover span { opacity: 1; transform: translateX(0); }
.plain-admin-sidebar-footer { border-top: 1px solid var(--ui-border); padding-top: .75rem; }
.plain-admin-profile-trigger, .plain-admin-avatar-button { align-items: center; background: var(--ui-secondary); border: 0; border-radius: 999px; color: var(--ui-secondary-foreground); display: flex; font-size: .72rem; font-weight: 700; height: 2.5rem; justify-content: center; overflow: hidden; width: 2.5rem; }
.plain-admin-profile-trigger img { height: 100%; object-fit: cover; width: 100%; }
.plain-admin-sidebar.is-desktop-expanded { align-items: stretch; box-shadow: var(--ui-shadow-overlay); width: var(--ui-sidebar-width); }
/* Offset the PNG's transparent inset so the visible mark aligns with menu icons. */
.plain-admin-sidebar.is-desktop-expanded .plain-admin-brand { align-self: flex-start; flex-shrink: 0; margin-left: -.15rem; }
.plain-admin-sidebar.is-desktop-expanded .plain-admin-brand { background: #ffffff; height: 3.25rem; max-width: 100%; width: 11rem; }
.plain-admin-sidebar.is-desktop-expanded .plain-admin-brand-icon { display: none; }
.plain-admin-sidebar.is-desktop-expanded .plain-admin-brand-full { display: block; height: auto; max-height: 3.25rem; width: 100%; }
.plain-admin-sidebar.is-desktop-expanded .plain-admin-navigation { align-items: stretch; }
.plain-admin-sidebar.is-desktop-expanded .plain-admin-nav-link { gap: .75rem; justify-content: flex-start; padding: 0 .75rem; width: 100%; }
.plain-admin-sidebar.is-desktop-expanded .plain-admin-nav-link span { background: transparent; border: 0; box-shadow: none; color: inherit; opacity: 1; padding: 0; pointer-events: auto; position: static; transform: none; }
@media (prefers-reduced-motion: reduce) {
  .plain-admin-sidebar, .plain-navigation-backdrop-enter-active, .plain-navigation-backdrop-leave-active { transition: none !important; }
}
.plain-admin-header { align-items: center; background: var(--ui-background); border: 1px solid var(--ui-border); border-bottom: 0; border-radius: var(--ui-radius) var(--ui-radius) 0 0; box-sizing: border-box; display: flex; height: var(--ui-header-height); left: var(--ui-frame-left); padding: 0 1.25rem; position: fixed; right: var(--ui-frame-gutter); top: var(--ui-frame-gutter); z-index: 70; }
.plain-admin-navigation-trigger { flex: 0 0 auto; }
.plain-admin-header-divider { background: var(--ui-border); height: 2rem; margin: 0 1.25rem; width: 1px; }
.plain-admin-header-navigation { align-items: center; display: flex; gap: 1.75rem; min-width: 0; }
.plain-admin-header-navigation a { color: var(--ui-muted-foreground); font-size: .875rem; font-weight: 500; text-decoration: none; white-space: nowrap; }
.plain-admin-header-navigation a:is(:hover, .is-active) { color: var(--ui-foreground); }
.plain-admin-header-navigation a.is-active { font-weight: 600; }
.plain-admin-search { align-items: center; color: var(--ui-muted-foreground); display: flex; margin-left: auto; max-width: 20rem; position: relative; width: 100%; }
.plain-admin-search > svg { left: .7rem; pointer-events: none; position: absolute; }
.plain-admin-search input { background: var(--ui-background); border: 1px solid var(--ui-input); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); font: inherit; height: 2.5rem; padding: 0 3.5rem 0 2.25rem; width: 100%; }
.plain-admin-search kbd { border: 1px solid var(--ui-border); border-radius: 4px; font-size: .65rem; font-weight: 600; padding: .12rem .3rem; position: absolute; right: .55rem; }
.plain-admin-search-results { background: var(--ui-popover); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); box-shadow: var(--ui-shadow-overlay); left: 0; padding: .35rem; position: absolute; right: 0; top: 3rem; }
.plain-admin-search-results button { align-items: center; background: transparent; border: 0; border-radius: calc(var(--ui-radius) - 3px); color: var(--ui-popover-foreground); display: flex; gap: .6rem; min-height: 2.25rem; padding: .4rem .55rem; text-align: left; width: 100%; }
.plain-admin-search-results button:hover { background: var(--ui-accent); }
.plain-admin-header-actions { align-items: center; display: flex; flex: 0 0 auto; gap: .5rem; margin-left: 1rem; }
.plain-admin-icon-button { align-items: center; background: var(--ui-background); border: 0; border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); display: flex; height: 2.5rem; justify-content: center; width: 2.5rem; }
.plain-admin-icon-button:hover { background: var(--ui-accent); }
.plain-admin-user-menu { background: var(--ui-popover); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); box-shadow: var(--ui-shadow-overlay); color: var(--ui-popover-foreground); min-width: 14rem; padding: .35rem; position: fixed; right: 1.5rem; top: 4.75rem; z-index: 110; }
.plain-admin-user-summary { border-bottom: 1px solid var(--ui-border); display: grid; gap: .15rem; margin-bottom: .3rem; padding: .55rem .65rem .7rem; }
.plain-admin-user-summary strong { font-size: .85rem; }
.plain-admin-user-summary small { color: var(--ui-muted-foreground); font-size: .72rem; }
.plain-admin-user-menu :is(a, button) { align-items: center; background: transparent; border: 0; border-radius: calc(var(--ui-radius) - 3px); color: inherit; display: flex; gap: .6rem; min-height: 2.25rem; padding: .45rem .65rem; text-decoration: none; width: 100%; }
.plain-admin-user-menu :is(a, button):hover { background: var(--ui-accent); }

:global(html), :global(body), :global(#app),
:global(.app-shell[data-ui-template="plain-admin"]) { background: var(--ui-background) !important; }
:global(.app-shell[data-ui-template="plain-admin"]) { color: var(--ui-foreground); font-family: var(--ui-font-sans); padding: 0 !important; }

@media (max-width: 1180px) {
  .plain-admin-header-navigation { display: none; }
  .plain-admin-header-divider { margin-right: 0; }
}

@media (max-width: 767px) {
  .plain-admin-sidebar { align-items: stretch; padding: 1rem; transform: translateX(-100%); transition: transform 180ms ease; width: 16rem; }
  .plain-admin-sidebar.is-mobile-open { transform: translateX(0); }
  .plain-admin-brand { align-self: flex-start; flex-shrink: 0; margin-left: -.15rem; }
  .plain-admin-sidebar .plain-admin-brand { background: #ffffff; height: 3.25rem; max-width: 100%; width: 11rem; }
  .plain-admin-sidebar .plain-admin-brand-icon { display: none; }
  .plain-admin-sidebar .plain-admin-brand-full { display: block; height: auto; max-height: 3.25rem; width: 100%; }
  .plain-admin-navigation { align-items: stretch; }
  .plain-admin-nav-link { gap: .75rem; height: 2.75rem; justify-content: flex-start; padding: 0 .75rem; width: 100%; }
  .plain-admin-nav-link span { background: transparent; border: 0; box-shadow: none; color: inherit; opacity: 1; padding: 0; pointer-events: auto; position: static; transform: none; }
  .plain-admin-sidebar-footer { align-items: center; display: flex; }
  .plain-admin-sidebar.is-desktop-expanded { width: 16rem; }
  .plain-admin-header { left: var(--ui-frame-gutter); right: var(--ui-frame-gutter); top: var(--ui-frame-gutter); }
  .plain-admin-navigation-trigger { margin-right: .5rem; }
  .plain-admin-header-divider { display: none; }
  .plain-admin-search { max-width: none; }
  .plain-admin-search kbd { display: none; }
  .plain-admin-search input { padding-right: .75rem; }
}

@media (max-width: 480px) {
  .plain-admin-header { padding: 0 .625rem; }
  .plain-admin-header-actions { gap: .125rem; margin-left: .25rem; }
  .plain-admin-icon-button, .plain-admin-avatar-button { height: 2.25rem; width: 2.25rem; }
  .plain-admin-search input { font-size: .8125rem; }
}
</style>
